/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { fromLatLng } from 'react-geocode';
import GoogleMapReact, { MapOptions } from 'google-map-react';

import { getAddressComponents } from '../../../Utilities/utils';

import { ControlPosition, ZoomControlStyle } from '../../../../Modules/Facility/Models/AddFacility.Model';
import { GoogleMapLocationSelectorProps } from './GoogleMapLocationSelector.Model';
import { SnackbarSeverity } from '../../../Constants/App';

const GoogleMap = ({
  currentLocation,
  setCurrentLocation,
  setCurrentAddress,
  zoomValue,
  setIsError,
  showSnackbar,
}: GoogleMapLocationSelectorProps): JSX.Element => {
  const { t } = useTranslation(['jobManagement']);

  const markerRef = useRef<google.maps.Marker | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  const createMapOptions = (maps: {
    ControlPosition: ControlPosition;
    ZoomControlStyle: ZoomControlStyle;
  }): MapOptions => {
    return {
      zoomControlOptions: {
        position: maps.ControlPosition.RIGHT_CENTER,
      },
      mapTypeControlOptions: {
        position: maps.ControlPosition.TOP_RIGHT,
      },
      mapTypeControl: true,
    };
  };

  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.setPosition(currentLocation);
    }
    if (mapRef.current) {
      mapRef.current.panTo(currentLocation);
    }
  }, [currentLocation]);

  const handleApiKey = () => {
    try {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: currentLocation }, (results, status) => {
        if (Array.isArray(results) && results?.length > 0) {
          const addressComponents = getAddressComponents(results);

          const isEmptyAddress =
            !addressComponents?.formattedAddress &&
            !addressComponents?.googlePlaceId &&
            !addressComponents?.latitude &&
            !addressComponents?.longitude;

          if (isEmptyAddress) {
            setIsError?.(true);
            showSnackbar?.(SnackbarSeverity.error, t('googleMap:notValidFacility'));
          } else {
            setIsError?.(false);
          }

          setCurrentAddress(addressComponents);
        } else {
          console.error(t('googleMap:errorWhileFetchingAddress'), status);
        }
      });
    } catch (error) {
      console.error(t('googleMap:errorWhileFetchingAddress'), error);
    }
  };

  useEffect(() => {
    handleApiKey();
  }, [currentLocation]);

  return (
    <GoogleMapReact
      bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string, libraries: ['places'] }}
      defaultCenter={{
        lat: currentLocation?.lat,
        lng: currentLocation?.lng,
      }}
      defaultZoom={zoomValue}
      options={createMapOptions}
      yesIWantToUseGoogleMapApiInternals
      onGoogleApiLoaded={({ map, maps }) => {
        mapRef.current = map;
        // If marker already exists, just update position
        if (markerRef.current) {
          markerRef.current.setPosition(currentLocation);
          return;
        }

        const marker = new maps.Marker({
          position: currentLocation,
          map,
          draggable: true,
          icon: {
            url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
          },
        });

        marker.addListener('dragend', (e: google.maps.MapMouseEvent) => {
          const lat = e.latLng?.lat();
          const lng = e.latLng?.lng();

          if (lat && lng) {
            setCurrentLocation({ lat, lng });

            fromLatLng(lat, lng, process.env.REACT_APP_GOOGLE_MAPS_API_KEY)
              // eslint-disable-next-line promise/always-return
              .then(({ results }) => {
                const addressComponents = getAddressComponents(results);
                setCurrentAddress(addressComponents);
                setCurrentLocation({ lat, lng });
              })
              .catch((error) => {
                console.error(t('googleMap:errorWhileFetchingAddress'), error);
              });
          }
        });

        markerRef.current = marker;
      }}></GoogleMapReact>
  );
};

export default GoogleMap;
